import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { DeclineContext, Transaction } from '../../../services/types';
import { getCurrencySymbol } from '../../../services/utils';
import {
  QuasiBody,
  QuasiCancelDialog,
  QuasiPrimaryButton,
  QuasiSecondaryButton,
  QuasiShell,
} from '../components/QuasiShell';
import { QuasiSessionExpiredPage, QuasiWaitingPage } from './QuasiStatusPage';
import { isOfferOverdue } from '../../../services/stateTimeout';

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  color: var(--ink);
  text-wrap: pretty;
`;

const Text = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: var(--ink-muted);
  text-wrap: pretty;
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 26px;
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 30px;
`;

const Tile = styled.button<{ $selected: boolean }>`
  height: 52px;
  padding: 0 8px;
  border: 1px solid ${({ $selected }) => ($selected ? 'var(--primary)' : 'var(--line-strong)')};
  border-radius: var(--r-md);
  background: var(--surface);
  box-shadow: ${({ $selected }) => ($selected ? '0 0 0 3px var(--primary-soft)' : 'none')};
  color: var(--ink);
  font-family: var(--font-sans);
  font-feature-settings: 'tnum';
  font-size: 18px;
  font-weight: 500;
  letter-spacing: -0.02em;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const PayButton = styled(QuasiPrimaryButton)`
  text-transform: uppercase;
`;

const Timer = styled.div<{ $tone: 'normal' | 'warn' | 'expired' }>`
  /* On a phone the frame is as tall as the screen: the timer stays at its bottom, not mid-page. */
  margin-top: auto;
  padding: 0 20px 12px;
  text-align: center;
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: -0.005em;
  color: ${({ $tone }) =>
    $tone === 'expired' ? 'var(--error)' : $tone === 'warn' ? 'var(--warn)' : 'var(--ink-faint)'};
`;

/** The last seconds of the offer are shown in the warning colour. */
const OFFER_WARN_SECONDS = 30;

const secondsLeft = (expireAt?: string): number | null => {
  if (!expireAt) return null;
  const ms = new Date(expireAt).getTime() - Date.now();
  return ms > 0 ? Math.floor(ms / 1000) : 0;
};

const mmss = (s: number) =>
  `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

const money = (amount: number, currency?: string) =>
  `${amount.toLocaleString('ru-RU')} ${getCurrencySymbol(currency) ?? currency ?? ''}`.trim();

interface Props {
  transaction: Transaction;
  /** Cancel as a refusal of the offered amounts. */
  onDeclineOffer: (context: DeclineContext) => Promise<void> | void;
  onSelectVariant?: (variantId: number) => void;
  variantSubmitting?: boolean;
  onBack: () => void;
  /** The last deadline the backend named for this offer. */
  offerExpireAt?: string;
}

const QuasiVariantPage = ({ transaction, onDeclineOffer, onSelectVariant, variantSubmitting, onBack, offerExpireAt }: Props) => {
  const { t } = useTranslation();
  const tx = transaction as any;
  const variants: { id: number; amount: number }[] = tx.variants ?? [];
  const currency: string | undefined = tx.currency ?? tx.requisite?.currency;

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [left, setLeft] = useState<number | null>(secondsLeft(offerExpireAt));
  const [overdue, setOverdue] = useState(isOfferOverdue(offerExpireAt));
  const [declining, setDeclining] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  useEffect(() => {
    const tick = () => {
      setLeft(secondsLeft(offerExpireAt));
      setOverdue(isOfferOverdue(offerExpireAt));
    };
    tick();
    if (!offerExpireAt) return undefined;
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [offerExpireAt]);

  const decline = async () => {
    if (declining) return;
    setDeclining(true);
    try {
      await onDeclineOffer('variants');
    } finally {
      setDeclining(false);
    }
  };

  // An expired offer can no longer be selected: the server refuses it, whatever the page shows.
  const offerClosed = left === 0;
  const selected = offerClosed ? undefined : variants.find((v) => v.id === selectedId);

  if (overdue) {
    return <QuasiSessionExpiredPage onBack={onBack} />;
  }

  // The offer has been expired and its renewal has not reached the ticket yet.
  if (variants.length === 0) {
    return <QuasiWaitingPage />;
  }

  return (
    <QuasiShell
      fitContent
      overlay={
        confirmingCancel && (
          <QuasiCancelDialog
            title={t('quasi.cancelTitle')}
            text={t('quasi.cancelText')}
            continueLabel={t('quasi.cancelContinue')}
            cancelLabel={t('quasi.cancelConfirm')}
            cancelling={declining}
            onContinue={() => setConfirmingCancel(false)}
            onCancel={decline}
          />
        )
      }
    >
      <QuasiBody>
        <Intro>
          <Title>{t('quasi.selectTitle')}</Title>
          <Text>{t('quasi.selectText', { amount: money(tx.amount, currency) })}</Text>
        </Intro>

        <Form>
          <Grid>
            {variants.map((v) => (
              <Tile
                key={v.id}
                type='button'
                data-sentry-element='onepage-variant-tile'
                aria-pressed={selected?.id === v.id}
                $selected={selected?.id === v.id}
                disabled={variantSubmitting || offerClosed}
                onClick={() => setSelectedId(v.id)}
              >
                {money(v.amount, currency)}
              </Tile>
            ))}
          </Grid>

          <PayButton
            type='button'
            data-sentry-element='onepage-variant-pay'
            disabled={!selected || variantSubmitting}
            onClick={() => selected && onSelectVariant?.(selected.id)}
          >
            {selected ? t('quasi.payAmount', { amount: money(selected.amount, currency) }) : t('quasi.pay')}
          </PayButton>
          <QuasiSecondaryButton
            type='button'
            disabled={declining || variantSubmitting}
            onClick={() => setConfirmingCancel(true)}
          >
            {t('quasi.cancel')}
          </QuasiSecondaryButton>
        </Form>

        {left !== null && (
          <Timer $tone={left === 0 ? 'expired' : left <= OFFER_WARN_SECONDS ? 'warn' : 'normal'}>
            {t('quasi.timeLeft', { time: mmss(left) })}
          </Timer>
        )}
      </QuasiBody>
    </QuasiShell>
  );
};

export default QuasiVariantPage;
